import { useEffect, useMemo, useRef, useState } from "react";
import { Radar } from "lucide-react";
import { sfx } from "../../lib/sound";
import { useConsole } from "../../hooks/useConsole";
import { CONFIRM_ID, mergeMap } from "../../lib/console";
import { buildSequence, pickDial, pickRiddle, sequenceLength } from "../../lib/lock";
import { ClarityField } from "./ClarityField";
import { LockSequence } from "./LockSequence";
import { LockRiddle } from "./LockRiddle";

const BASE = 0.12;

// Gate before a new memory: the hive cannot place it; the audience clarifies it on the console.
export const MemoryLock = ({ lock, challenges, consoleMap, consoleDevices, forceTick, confirmTick, onUnlocked }) => {
  const [attempt, setAttempt] = useState(1);
  const [fails, setFails] = useState(0);
  const [result, setResult] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [lastEvent, setLastEvent] = useState(null);
  const done = useRef(false);
  const unlock = useRef(onUnlocked);
  unlock.current = onUnlocked;

  const mapRef = useRef(mergeMap(consoleMap));
  const { map, dials, connected } = useConsole({
    map: consoleMap, devices: consoleDevices, enabled: true,
    onEvent: (ev) => {
      if (ev.id && mapRef.current[ev.id]?.enabled === false) return;
      const e = ev.type === "press" && ev.id === CONFIRM_ID ? { type: "confirm", id: ev.id, name: ev.name } : ev;
      setLastEvent({ ...e, t: Date.now() });
      window.dispatchEvent(new Event("pointerdown"));
    },
  });
  mapRef.current = map;
  const connectedRef = useRef(connected);
  connectedRef.current = connected;

  const challenge = useMemo(() => {
    if (lock.kind === "sequence") return { kind: "sequence", length: sequenceLength(challenges, lock.decodedCount) };
    return { kind: "riddle", riddle: pickRiddle(lock.specimen, lock.decodedSpecimens, lock.decodedCount), dial: pickDial(map) };
  }, [lock.at]); // eslint-disable-line react-hooks/exhaustive-deps

  const makeSteps = () => buildSequence(map, challenge.length, Object.keys(connectedRef.current));

  const finish = (gaveIn) => {
    if (done.current) return;
    done.current = true;
    setResult(gaveIn ? "givein" : "success");
    setProgress(1);
    gaveIn ? sfx.known() : sfx.clarify();
    setTimeout(() => unlock.current({ gaveIn, fails }), gaveIn ? 1800 : 1600);
  };

  const onResult = (ok) => {
    if (ok) { finish(false); return; }
    sfx.wrong();
    setResult("fail");
    const n = fails + 1;
    setFails(n);
    setTimeout(() => {
      if (n >= (challenges?.give_in_after ?? 3)) finish(true);
      else { setResult("idle"); setAttempt((a) => a + 1); }
    }, 2200);
  };

  const startTicks = useRef({ force: forceTick, confirm: confirmTick });
  useEffect(() => { if (forceTick !== startTicks.current.force) finish(true); }, [forceTick]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (confirmTick !== startTicks.current.confirm) setLastEvent({ type: "confirm", id: "key", name: "Enter", t: Date.now() }); }, [confirmTick]);

  const clarity = result === "success" || result === "givein" ? 1 : BASE + progress * (0.5 - BASE);
  const showHint = fails >= 2;
  const giveIn = challenges?.give_in_after ?? 3;
  const status = {
    idle: fails ? `Attempt ${attempt} · the spores recoiled ${fails}×` : "The hive cannot place this memory. Help it.",
    fail: "Wrong path. The spores recoil…",
    success: "Signal clear. The memory surfaces.",
    givein: "The hive gives in. It lets the memory go…",
  }[result];

  return (
    <div data-testid="memory-lock" data-kind={challenge.kind} data-result={result} data-fails={fails} className="fixed inset-0 z-40 flex items-center justify-center bg-[#050807]/92 p-6 backdrop-blur-xl">
      <div className={`lab-dialog w-full max-w-4xl p-7 sm:p-9 transition-shadow duration-300 ${result === "fail" ? "shadow-[0_0_80px_rgba(244,63,94,0.35)]" : result === "success" ? "shadow-[0_0_90px_rgba(34,211,238,0.45)]" : ""}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-cyan-300"><Radar size={14} className="animate-pulse" /> Specimen #{String(lock.specimen.slot).padStart(2, "0")} · locating memory in the spores</p>
          <p data-testid="lock-status" className={`font-mono text-xs uppercase tracking-widest ${result === "fail" ? "text-rose-300" : result === "success" ? "text-emerald-300" : "text-slate-400"}`}>{status}</p>
        </div>
        <div className="mt-5 grid gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
          <ClarityField specimen={lock.specimen} clarity={clarity} />
          <div>
            {challenge.kind === "sequence" ? (
              <LockSequence makeSteps={makeSteps} length={challenge.length} giveIn={giveIn} lastEvent={lastEvent} showHint={showHint} attempt={attempt} onProgress={setProgress} onResult={onResult} />
            ) : (
              <LockRiddle riddle={challenge.riddle} dial={challenge.dial} dialValue={dials[challenge.dial?.id]} giveIn={giveIn} lastEvent={lastEvent} showHint={showHint} attempt={attempt} onProgress={setProgress} onResult={onResult} />
            )}
          </div>
        </div>
        <p className="mt-6 font-mono text-[11px] uppercase tracking-widest text-slate-400">Use the console on the bench · Enter locks in · the operator can release it with U</p>
      </div>
    </div>
  );
};
